import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const FEEDBACK_RESET_DELAY = 2200;

class KharmaQuickSize extends Component {
  requiredRefs = ['status', 'toggle', 'panel'];

  #controller = new AbortController();
  #feedbackTimeout;
  #pendingSelection;
  #submitting = false;
  #desktopMedia = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.#submitting = false;

    const { signal } = this.#controller;
    const productCard = this.closest('product-card');

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    // Available swatches stop the bubbling change event in Horizon.
    productCard?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    productCard?.addEventListener('change', this.#syncColor, { signal, capture: true });
    this.#desktopMedia.addEventListener('change', this.#syncPanelInteractivity, { signal });

    this.#syncColor();
    this.#syncPanelInteractivity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    if (this.#feedbackTimeout) clearTimeout(this.#feedbackTimeout);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    event.stopPropagation();

    const color = event.target.closest('[data-quick-size-color]');
    if (color) {
      if (this.#submitting) return;
      const swatch = Array.from(this.closest('product-card')?.querySelectorAll(
        'swatches-variant-picker-component input'
      ) || []).find((input) => input.dataset.optionValueId === color.dataset.optionValueId);
      this.dataset.initialColorGroup = color.dataset.quickSizeColor;
      if (swatch) swatch.click();
      this.#syncColor();
      return;
    }

    const toggle = event.target.closest('[data-quick-size-toggle]');
    if (toggle) {
      this.#setOpen(this.dataset.open !== 'true');
      return;
    }

    const close = event.target.closest('[data-quick-size-close]');
    if (close) {
      this.#setOpen(false);
      this.refs.toggle.focus();
      return;
    }
  };

  #handleProductSelect = (event) => {
    this.#syncColor();
    // Settle after Horizon's form has applied the color response. Its pending-add
    // queue resolves the swatch's first variant, not the clicked quick size.
    this.#pendingSelection = event.promise?.then(() => true, () => false);
  };

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    event.preventDefault();
    if (this.#submitting) return;
    const sizeButton = event.submitter;
    const colorGroup = this.#selectedColorGroup();
    const isExactSelection = () =>
      sizeButton instanceof HTMLButtonElement &&
      this.contains(sizeButton) &&
      !sizeButton.disabled &&
      sizeButton.closest('[data-quick-size-group]')?.dataset.quickSizeGroup === colorGroup &&
      this.#selectedColorGroup() === colorGroup;

    // Never submit a stale/hidden color, an unavailable size, or a default ID.
    if (!isExactSelection()) return;

    const variantId = sizeButton.dataset.quickSizeVariant;
    if (!variantId) return;

    this.#submitting = true;
    this.setAttribute('aria-busy', 'true');

    this.querySelectorAll('[data-quick-size-variant]').forEach((button) => button.removeAttribute('data-loading'));
    sizeButton.setAttribute('data-loading', 'true');
    this.refs.status.textContent = this.dataset.addingText || 'Adding…';

    let pending;
    let resolved = true;
    do {
      pending = this.#pendingSelection;
      if (pending) resolved = await pending;
    } while (pending !== this.#pendingSelection);

    if (!this.isConnected) return;
    if (!resolved || !isExactSelection()) {
      this.#finishSubmission();
      sizeButton.removeAttribute('data-loading');
      this.refs.status.textContent = this.dataset.errorText || 'Unavailable';
      return;
    }

    const variantIdInput = this.querySelector('input[name="id"]');
    const productForm = this.querySelector('product-form-component');
    if (!(variantIdInput instanceof HTMLInputElement) || !productForm?.handleSubmit) {
      this.#finishSubmission();
      this.refs.status.textContent = this.dataset.errorText;
      return;
    }

    // The server-rendered size button maps this color + size to one exact ID.
    // Keep Horizon's cart submission, events, feedback, and drawer unchanged.
    variantIdInput.value = variantId;
    variantIdInput.disabled = false;
    try {
      productForm.handleSubmit(event);
    } catch {
      this.#finishSubmission();
      sizeButton.removeAttribute('data-loading');
      this.refs.status.textContent = this.dataset.errorText;
    }
  }

  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || this.dataset.open !== 'true') return;
    this.#setOpen(false);
    this.refs.toggle.focus();
  };

  #selectedColorGroup() {
    const selectedSwatch = Array.from(this.closest('product-card')?.querySelectorAll(
      'swatches-variant-picker-component input:checked'
    ) || []).find((input) => input.dataset.optionName === this.dataset.colorOptionName);
    return selectedSwatch?.value || this.dataset.initialColorGroup || 'default';
  }

  #syncColor = () => {
    const colorGroup = this.#selectedColorGroup();
    let activePanel;

    this.querySelectorAll('[data-quick-size-color]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.quickSizeColor === colorGroup));
    });

    this.querySelectorAll('[data-quick-size-group]').forEach((panel) => {
      const isActive = panel.getAttribute('data-quick-size-group') === colorGroup;
      panel.toggleAttribute('hidden', !isActive);
      if (isActive) activePanel = panel;
    });

    if (!activePanel) return;

    const availableCount = activePanel.querySelectorAll('[data-quick-size-variant]:not(:disabled)').length;
    this.toggleAttribute('data-current-color-sold-out', availableCount === 0);
    this.#resetFeedback();
  };

  #handleCartUpdate = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        this.#finishSubmission();
        const didError = detail?.didError === true;
        this.dataset.state = didError ? 'error' : 'added';
        this.refs.status.textContent = didError
          ? this.dataset.errorText || 'Unavailable'
          : this.dataset.addedText || 'Added';
        this.querySelectorAll('[data-loading]').forEach((button) => button.removeAttribute('data-loading'));

        if (!didError) this.#setOpen(false);

        if (this.#feedbackTimeout) clearTimeout(this.#feedbackTimeout);
        this.#feedbackTimeout = setTimeout(() => this.#resetFeedback(), FEEDBACK_RESET_DELAY);
      })
      .catch(() => {
        this.#finishSubmission();
        this.dataset.state = 'error';
        this.refs.status.textContent = this.dataset.errorText || 'Unavailable';
        this.querySelectorAll('[data-loading]').forEach((button) => button.removeAttribute('data-loading'));
      });
  };

  #setOpen(open) {
    this.dataset.open = open ? 'true' : 'false';
    this.refs.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.#syncPanelInteractivity();
  }

  #finishSubmission() {
    this.#submitting = false;
    this.removeAttribute('aria-busy');
  }

  #syncPanelInteractivity = () => {
    const isHiddenOnMobile = !this.#desktopMedia.matches && this.dataset.open !== 'true';
    this.refs.panel.toggleAttribute('inert', isHiddenOnMobile);
    this.refs.panel.toggleAttribute('aria-hidden', isHiddenOnMobile);
  };

  #resetFeedback() {
    delete this.dataset.state;
    this.querySelectorAll('[data-loading]').forEach((button) => button.removeAttribute('data-loading'));
    this.refs.status.textContent = this.hasAttribute('data-current-color-sold-out')
      ? this.dataset.soldOutText || 'Currently sold out'
      : this.dataset.promptText || 'Select size';
  }
}

if (!customElements.get('kharma-quick-size')) {
  customElements.define('kharma-quick-size', KharmaQuickSize);
}
